import { useRuntimeConfig } from '#imports'
import type { RouteRecordRaw } from 'vue-router'

import buybackEnabled from '@backmarket/nuxt-layer-buyback/app/middleware/buybackEnabled'
import { ROLES } from '@backmarket/nuxt-layer-buyback/app/scopes/order/components/OrderConversation/OrderConversation.constants'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'

import { ROUTES as AUTH_ROUTES } from '~/scopes/auth/route-names'
import redirectLegacyTradeInProduct from '~/scopes/buyback/middleware/redirectLegacyTradeInProduct/redirectLegacyTradeInProduct'
import { CMS } from '~/scopes/cms/routes-names'

import { ROUTE_NAMES } from './constants'

export default (): RouteRecordRaw[] => {
  const { market } = useMarketplace()
  const { FF_ENABLE_TRADE_IN_PRODUCT_PAGES } = useRuntimeConfig().public

  return [
    {
      path: 'buyback-funnel',
      component: () =>
        import('~/scopes/buyback/components/BuybackErrorBoundary/BuybackErrorBoundaryPage.vue'),
      meta: {
        layout: 'minimal',
        owners: ['bot-squad-circularity-offer-front'],
        middleware: [buybackEnabled],
        tracking: { contentGroup: 'Trade-In Funnel' },
        shoppingAssistant: { enabled: false },
      },
      children: [
        {
          name: ROUTE_NAMES.DEVICE_ASSESSMENT,
          path: 'device/:slug/:id',
          component: () =>
            import('~/scopes/buyback/pages/DeviceAssessment.vue'),
          meta: {
            owners: ['bot-squad-circularity-offer-front'],
          },
        },
        {
          name: ROUTE_NAMES.OFFER,
          path: 'device/:slug/:id/offer/:listingId',
          component: () => import('~/scopes/buyback/pages/Offer.vue'),
          meta: {
            owners: ['bot-squad-circularity-offer-front'],
          },
        },
        {
          name: ROUTE_NAMES.NO_OFFER,
          path: 'device/:slug/:id/no-offer',
          component: () => import('~/scopes/buyback/pages/NoOffer.vue'),
          meta: {
            owners: ['bot-squad-circularity-offer-front'],
          },
        },
        {
          name: ROUTE_NAMES.IMEI,
          path: 'device/:slug/:id/offer/:listingId/imei',
          component: () => import('~/scopes/buyback/pages/Imei.vue'),
          meta: {
            auth: {
              mode: ['authenticated'],
              redirection: {
                default: {
                  name: AUTH_ROUTES.AUTH.LOGIN,
                  query: { bm_journey: CMS.BUYBACK },
                },
              },
            },
            owners: ['bot-squad-circularity-offer-front'],
          },
        },
        {
          name: ROUTE_NAMES.BANK,
          path: 'device/:slug/:id/offer/:listingId/bank',
          component: () => import('~/scopes/buyback/pages/Bank.vue'),
          meta: {
            auth: {
              mode: ['authenticated'],
              redirection: {
                default: {
                  name: AUTH_ROUTES.AUTH.LOGIN,
                  query: { bm_journey: CMS.BUYBACK },
                },
              },
            },
            owners: ['bot-squad-circularity-offer-front'],
          },
        },
        {
          name: ROUTE_NAMES.ADDRESS,
          path: 'device/:slug/:id/offer/:listingId/address',
          component: () => import('~/scopes/buyback/pages/Address.vue'),
          meta: {
            auth: {
              mode: ['authenticated'],
              redirection: {
                default: {
                  name: AUTH_ROUTES.AUTH.LOGIN,
                  query: { bm_journey: CMS.BUYBACK },
                },
              },
            },
            owners: ['bot-squad-circularity-offer-front'],
          },
        },
        {
          name: ROUTE_NAMES.SHIPPING,
          path: 'device/:slug/:id/offer/:listingId/address/:addressId/shipping',
          component: () => import('~/scopes/buyback/pages/Shipping.vue'),
          meta: {
            auth: {
              mode: ['authenticated'],
              redirection: {
                default: {
                  name: AUTH_ROUTES.AUTH.LOGIN,
                  query: { bm_journey: CMS.BUYBACK },
                },
              },
            },
            owners: ['bot-squad-circularity-offer-front'],
          },
        },
        {
          name: ROUTE_NAMES.CONFIRMATION,
          path: 'confirmation/:id',
          component: () => import('~/scopes/buyback/pages/Confirmation.vue'),
          meta: {
            auth: {
              mode: ['authenticated'],
              redirection: {
                default: {
                  name: AUTH_ROUTES.AUTH.LOGIN,
                  query: { bm_journey: CMS.BUYBACK },
                },
              },
            },
            owners: ['bot-squad-circularity-offer-front'],
          },
        },
      ],
    },
    {
      name: ROUTE_NAMES.CONVERSATION,
      path: 'dashboard/sales/:id/conversation',
      component: () =>
        import('@backmarket/nuxt-layer-buyback/app/scopes/order/components/OrderConversation/OrderConversation.vue'),
      meta: {
        auth: {
          mode: ['authenticated'],
          redirection: {
            default: {
              name: AUTH_ROUTES.AUTH.LOGIN,
              query: { bm_journey: CMS.BUYBACK },
            },
          },
        },
        owners: ['bot-squad-circularity-offer-front'],
        layout: 'minimal',
        tracking: { contentGroup: 'Trade-In Funnel' },
        shoppingAssistant: { enabled: false },
      },
      props: {
        role: ROLES.CUSTOMER,
      },
    },
    {
      name: ROUTE_NAMES.TRADE_IN_PRODUCT,
      // `m` stands for model, and disambiguates this path from the legacy one below.
      path: 'buyback/m/:category/:slug',
      component: () => import('~/scopes/buyback/pages/ProductPage.vue'),
      meta: {
        owners: ['bot-squad-circularity-offer-front'],
        routeExists: () =>
          FF_ENABLE_TRADE_IN_PRODUCT_PAGES.includes(market.countryCode),
        middleware: [buybackEnabled],
        tracking: { contentGroup: 'Trade-In Discovery' },
        shoppingAssistant: { enabled: false },
      },
    },
    {
      // Always redirected by the middleware, but vue-router still requires a component.
      name: ROUTE_NAMES.TRADE_IN_PRODUCT_LEGACY,
      path: 'buyback/:category/:slug/:id',
      component: () => import('~/scopes/buyback/pages/ProductPage.vue'),
      meta: {
        owners: ['bot-squad-circularity-offer-front'],
        routeExists: () =>
          FF_ENABLE_TRADE_IN_PRODUCT_PAGES.includes(market.countryCode),
        middleware: [buybackEnabled, redirectLegacyTradeInProduct],
        tracking: { contentGroup: 'Trade-In Discovery' },
        shoppingAssistant: { enabled: false },
      },
    },
  ]
}
